import jsPDF from 'jspdf';
import html2canvas from 'html2canvas-pro';
import { calculateAge, formatDateEs } from './dateUtils';

// Helper function to generate questionnaire HTML
const generateQuestionnaireHTML = (questionnaire) => {
  if (!questionnaire) return '';
  
  // Generate family history section
  const generateFamilyHistoryHTML = () => {
    if (!questionnaire.familyHistory) return '';
    
    const familyEntries = Object.entries(questionnaire.familyHistory);
    if (familyEntries.length === 0) return '';
    
    const renderFamilyQuestion = (key, value) => `
      <div style="display: flex; justify-content: space-between; align-items: center; font-size: 10px; margin-bottom: 8px; border-bottom: 1px solid #eee; padding-bottom: 4px;">
        <span>${getQuestionnaireLabel('familyHistory', key)}</span>
        <div style="display: flex; gap: 8px;">
          <span style="background: ${value?.answer === true ? '#00c1c1' : '#f0f0f0'}; color: ${value?.answer === true ? 'white' : '#666'}; padding: 2px 8px; border-radius: 3px; font-size: 9px;">Sí</span>
          <span style="background: ${value?.answer === false ? '#00c1c1' : '#f0f0f0'}; color: ${value?.answer === false ? 'white' : '#666'}; padding: 2px 8px; border-radius: 3px; font-size: 9px;">No</span>
        </div>
      </div>
      ${value?.answer === true && value?.details ? `<div style="font-size: 9px; color: #666; margin-bottom: 5px; padding-left: 10px;">• ${value.details}</div>` : ''}
    `;
    
    const familyHistoryHTML = familyEntries.map(([key, value]) => renderFamilyQuestion(key, value)).join('');
    
    return `
      <div style="margin-top: 20px; page-break-inside: avoid; break-inside: avoid;" data-section="family-history">
        <h2 style="
          margin: 0 0 15px 0; 
          font-size: 14px; 
          font-weight: bold; 
          color: #2c3e50; 
          border-bottom: 2px solid #00c1c1; 
          padding-bottom: 5px;
          display: flex;
          align-items: center;
          gap: 8px;
          page-break-after: avoid;
        ">
          <span style="color: #00c1c1;">👨‍👩‍👧‍👦</span>
          ANTECEDENTES HEREDOFAMILIARES
        </h2>
        <div style="background-color: #fafafa; padding: 15px; border-radius: 8px; border: 1px solid #e9ecef; page-break-inside: avoid; break-inside: avoid;">
          ${familyHistoryHTML}
        </div>
      </div>
    `;
  };
  
  const pathologicalEntries = Object.entries(questionnaire.pathological || {});
  const midPoint = Math.ceil(pathologicalEntries.length / 2);
  const firstHalf = pathologicalEntries.slice(0, midPoint);
  const secondHalf = pathologicalEntries.slice(midPoint);
  
  const renderQuestion = (key, value, section = 'pathological') => `
    <div style="display: flex; justify-content: space-between; align-items: center; font-size: 10px; margin-bottom: 8px; border-bottom: 1px solid #eee; padding-bottom: 4px;">
      <span>${getQuestionnaireLabel(section, key)}</span>
      <div style="display: flex; gap: 8px;">
        <span style="background: ${value?.answer === true ? '#00c1c1' : '#f0f0f0'}; color: ${value?.answer === true ? 'white' : '#666'}; padding: 2px 8px; border-radius: 3px; font-size: 9px;">Sí</span>
        <span style="background: ${value?.answer === false ? '#00c1c1' : '#f0f0f0'}; color: ${value?.answer === false ? 'white' : '#666'}; padding: 2px 8px; border-radius: 3px; font-size: 9px;">No</span>
      </div>
    </div>
    ${value?.answer === true && value?.details ? `<div style="font-size: 9px; color: #666; margin-bottom: 5px; padding-left: 10px;">• ${value.details}</div>` : ''}
  `;
  
  const firstColumn = firstHalf.map(([key, value]) => renderQuestion(key, value)).join('');
  const secondColumn = secondHalf.map(([key, value]) => renderQuestion(key, value)).join('');
  
  // Special lifestyle questions
  const embarazoHTML = questionnaire.lifestyle?.embarazo ? `
    <div style="display: flex; justify-content: space-between; align-items: center; font-size: 10px; margin-bottom: 8px; border-bottom: 1px solid #eee; padding-bottom: 4px;">
      <span>${getQuestionnaireLabel('lifestyle', 'embarazo')}</span>
      <div style="display: flex; gap: 8px; align-items: center;">
        <span style="background: ${questionnaire.lifestyle.embarazo.answer === true ? '#00c1c1' : '#f0f0f0'}; color: ${questionnaire.lifestyle.embarazo.answer === true ? 'white' : '#666'}; padding: 2px 8px; border-radius: 3px; font-size: 9px;">Sí</span>
        <span style="background: ${questionnaire.lifestyle.embarazo.answer === false ? '#00c1c1' : '#f0f0f0'}; color: ${questionnaire.lifestyle.embarazo.answer === false ? 'white' : '#666'}; padding: 2px 8px; border-radius: 3px; font-size: 9px;">No</span>
        ${questionnaire.lifestyle.embarazo.meses ? `<span style="font-size: 9px; color: #666;">Meses: ${questionnaire.lifestyle.embarazo.meses}</span>` : ''}
      </div>
    </div>
  ` : '';
  
  const cancerHTML = questionnaire.lifestyle?.cancer ? renderQuestion('cancer', questionnaire.lifestyle.cancer, 'lifestyle') : '';

  // New questions after cancer
  const bifosfonatosHTML = questionnaire.lifestyle?.bifosfonatos ? renderQuestion('bifosfonatos', questionnaire.lifestyle.bifosfonatos, 'lifestyle') : '';
  const cirugiasPreviasHTML = questionnaire.lifestyle?.cirugiasPrevias ? renderQuestion('cirugiasPrevias', questionnaire.lifestyle.cirugiasPrevias, 'lifestyle') : '';
  const otraEnfermedadHTML = questionnaire.lifestyle?.otraEnfermedad ? renderQuestion('otraEnfermedad', questionnaire.lifestyle.otraEnfermedad, 'lifestyle') : '';

  // Other lifestyle questions
  const lifestyleEntries = Object.entries(questionnaire.lifestyle || {}).filter(([key]) => !['embarazo', 'cancer', 'bifosfonatos', 'cirugiasPrevias', 'otraEnfermedad'].includes(key));
  const lifestyleHTML = lifestyleEntries.map(([key, value]) => renderQuestion(key, value, 'lifestyle')).join('');
  
  const ultimaConsultaHTML = `
    <div style="border-top: 1px solid #ddd; padding-top: 12px; margin-bottom: 12px;">
      <div style="font-size: 10px; font-weight: 500; color: #333; margin-bottom: 6px;">Fecha y motivo de la última consulta odontológica:</div>
      <div style="font-size: 10px; color: #333; background: white; padding: 8px; border-radius: 4px; border: 1px solid #ddd; min-height: 32px;">${questionnaire.current?.ultimaConsultaOdontologica || ''}</div>
    </div>
  `;

  const currentConditionHTML = `
    <div style="border-top: 1px solid #ddd; padding-top: 12px;">
      <div style="font-size: 10px; font-weight: 500; color: #333; margin-bottom: 6px;">Padecimiento actual (motivo de consulta):</div>
      <div style="font-size: 10px; color: #333; background: white; padding: 8px; border-radius: 4px; border: 1px solid #ddd; min-height: 48px;">${questionnaire.current?.padecimientoActual || ''}</div>
    </div>
  `;
  
  return `
    ${generateFamilyHistoryHTML()}
    
    <div style="margin-top: 20px; page-break-inside: avoid; break-inside: avoid;" data-section="questionnaire-part1">
      <h2 style="margin: 0 0 12px 0; font-size: 12px; font-weight: 500; color: #333; border-left: 3px solid #00c1c1; padding-left: 8px;"></h2>
      <h2 style="
        margin: 0 0 15px 0; 
        font-size: 14px; 
        font-weight: bold; 
        color: #2c3e50; 
        border-bottom: 2px solid #00c1c1; 
        padding-bottom: 5px;
        display: flex;
        align-items: center;
        gap: 8px;
        page-break-after: avoid;
      ">
        <span style="color: #00c1c1;">🩺</span>
        ANTECEDENTES PATOLÓGICOS Y NO PATOLÓGICOS
      </h2>
      <div style="background-color: #fafafa; padding: 15px; border-radius: 8px; border: 1px solid #e9ecef; page-break-inside: avoid; break-inside: avoid;">
        
        <!-- Pathological conditions grid -->
        <div style="display: flex; justify-content: space-between; margin-bottom: 15px; page-break-inside: avoid; break-inside: avoid;">
          <div style="flex: 1; margin-right: 20px;">
            ${firstColumn}
          </div>
          <div style="flex: 1;">
            ${secondColumn}
            ${embarazoHTML}
            ${cancerHTML}
            ${bifosfonatosHTML}
            ${cirugiasPreviasHTML}
            ${otraEnfermedadHTML}
          </div>
        </div>
      </div>
      
      <!-- Force page break opportunity -->
      <div style="page-break-after: auto; break-after: auto;"></div>
    </div>
    
    <!-- Second part without header for lifestyle questions and current condition -->
    <div style="margin-top: 10px; page-break-inside: avoid; break-inside: avoid;" data-section="questionnaire-part2">
      <div style="background-color: #fafafa; padding: 15px; border-radius: 8px; border: 1px solid #e9ecef; page-break-inside: avoid; break-inside: avoid;">
        
        <!-- Lifestyle questions -->
        <div style="margin-bottom: 12px; page-break-inside: avoid; break-inside: avoid;">
          ${lifestyleHTML}
        </div>

        <!-- Last dental visit -->
        ${ultimaConsultaHTML}

        <!-- Current condition -->
        ${currentConditionHTML}
      </div>
    </div>
  `;
};

// Helper function to get user-friendly labels for questionnaire fields
const getQuestionnaireLabel = (section, key) => {
  const labels = {
    familyHistory: {
      padre: 'Padre',
      madre: 'Madre',
      abuelos_paternos: 'Abuelos paternos',
      abuelos_maternos: 'Abuelos maternos'
    },
    pathological: {
      hipertension: 'Hipertensión',
      enfermedadesCorazon: 'Enfermedades del corazón',
      diabetes: 'Diabetes',
      vih: 'VIH',
      artritis: 'Artritis',
      convulsiones: 'Convulsiones',
      infartosMiocardio: 'Infartos al miocardio',
      alergiaAnestesicos: 'Alergia a anestésicos',
      alergiaAntibioticos: 'Alergia a medicamentos',
      discrepanciasSanguineas: 'Discrepancias sanguíneas',
      enfViasRespiratorias: 'Enf. vías respiratorias',
      hepatitis: 'Hepatitis',
      epilepsia: 'Epilepsia'
    },
    lifestyle: {
      bajoTratamientoMedico: '¿Está bajo tratamiento médico?',
      fuma: '¿Fuma?',
      consumeAlcohol: '¿Consume bebidas alcohólicas?',
      consumeSustanciasPsicoactivas: '¿Consume sustancias psicoactivas?',
      enjuagueBucal: '¿Utiliza enjuague bucal?',
      hiloDental: '¿Utiliza hilo dental?',
      embarazo: 'Embarazo',
      cancer: 'Cáncer',
      bifosfonatos: '¿Está tomando bifosfonatos?',
      cirugiasPrevias: '¿Cirugías previas?',
      otraEnfermedad: '¿Tiene alguna enfermedad que no se haya mencionado?'
    }
  };
  
  return labels[section]?.[key] || key;
};

// Helper function to get gender label in Spanish
const getGenderLabel = (gender) => {
  const genderMap = {
    'male': 'Masculino',
    'female': 'Femenino',
    'other': 'Otro'
  };
  return genderMap[gender] || 'No especificado';
};

// Helper function to generate physical exam HTML
const generatePhysicalExamHTML = (physicalExam) => {
  if (!physicalExam) return '';
  
  const examFields = [
    { key: 'labios', label: 'Labios' },
    { key: 'lengua', label: 'Lengua' },
    { key: 'carrillos', label: 'Carrillos' },
    { key: 'paladarDuro', label: 'Paladar duro' },
    { key: 'paladarBlando', label: 'Paladar blando' },
    { key: 'cabezaYCuello', label: 'Cabeza y cuello' },
    { key: 'pisoDeBoca', label: 'Piso de boca' },
    { key: 'glandSalivales', label: 'Gland. salivales' },
    { key: 'ganglios', label: 'Ganglios' },
    { key: 'parodonto', label: 'Parodonto' },
    { key: 'oclusion', label: 'Oclusión' },
    { key: 'atm', label: 'ATM' }
  ];
  
  const midPoint = Math.ceil(examFields.length / 2);
  const firstHalf = examFields.slice(0, midPoint);
  const secondHalf = examFields.slice(midPoint);
  
  const renderField = (field) => {
    const value = physicalExam[field.key] || '';
    return `
      <div style="margin-bottom: 12px; border-bottom: 1px solid #eee; padding-bottom: 8px;">
        <div style="font-weight: bold; font-size: 10px; color: #2c3e50; margin-bottom: 4px;">
          ${field.label}
        </div>
        <div style="font-size: 9px; color: #555; min-height: 20px; background: #f8f9fa; padding: 4px 6px; border-radius: 3px;">
          ${value || 'Sin observaciones'}
        </div>
      </div>
    `;
  };
  
  const firstColumn = firstHalf.map(renderField).join('');
  const secondColumn = secondHalf.map(renderField).join('');
  
  return `
    <div style="margin-top: 20px; page-break-inside: avoid; break-inside: avoid;" data-section="physical-exam">
      <!-- Blue separator line -->
      <div style="
        height: 3px; 
        background: linear-gradient(90deg, #00c1c1, #0099aa); 
        margin: 15px 0; 
        border-radius: 2px;
        box-shadow: 0 1px 2px rgba(0, 193, 193, 0.3);
      "></div>
      
      <h2 style="
        margin: 0 0 15px 0; 
        font-size: 14px; 
        font-weight: bold; 
        color: #2c3e50; 
        border-bottom: 2px solid #00c1c1; 
        padding-bottom: 5px;
        display: flex;
        align-items: center;
        gap: 8px;
        page-break-after: avoid;
      ">
        <span style="color: #00c1c1;">🔍</span>
        EXPLORACIÓN FÍSICA ODONTOLÓGICA
      </h2>
      
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; page-break-inside: avoid; break-inside: avoid;">
        <div>${firstColumn}</div>
        <div>${secondColumn}</div>
      </div>
    </div>
  `;
};

// Helper function to generate vital signs HTML
const generateVitalSignsHTML = (vitalSigns) => {
  if (!vitalSigns) return '';
  
  const vitalSignsFields = [
    { key: 'peso', label: 'Peso' },
    { key: 'talla', label: 'Talla' },
    { key: 'temperatura', label: 'Temperatura' },
    { key: 'tensionArterial', label: 'Tensión Arterial' },
    { key: 'frecuenciaCardiaca', label: 'Frecuencia Cardíaca' },
    { key: 'glicemia', label: 'Glicemia' },
    { key: 'frecuenciaRespiratoria', label: 'Frecuencia Respiratoria' }
  ];
  
  // Check if there's any content in vital signs
  const hasContent = vitalSignsFields.some(field => vitalSigns[field.key] && vitalSigns[field.key].trim());
  if (!hasContent) return '';
  
  const fieldsHTML = vitalSignsFields.map(field => {
    const value = vitalSigns[field.key] || '';
    return `
      <div style="
        border: 1px solid #ddd; 
        padding: 8px; 
        text-align: center; 
        background: ${value ? '#f8f9fa' : '#fff'};
        min-height: 40px;
        display: flex;
        flex-direction: column;
        justify-content: center;
      ">
        <div style="font-weight: bold; font-size: 9px; color: #2c3e50; margin-bottom: 4px;">
          ${field.label}
        </div>
        <div style="font-size: 11px; color: #555;">
          ${value || ''}
        </div>
      </div>
    `;
  }).join('');
  
  return `
    <div style="margin-top: 20px; page-break-inside: avoid; break-inside: avoid;" data-section="vital-signs">
      <!-- Blue separator line -->
      <div style="
        height: 3px; 
        background: linear-gradient(90deg, #00c1c1, #0099aa); 
        margin: 15px 0; 
        border-radius: 2px;
        box-shadow: 0 1px 2px rgba(0, 193, 193, 0.3);
      "></div>
      
      <h2 style="
        margin: 0 0 15px 0; 
        font-size: 14px; 
        font-weight: bold; 
        color: #2c3e50; 
        border-bottom: 2px solid #00c1c1; 
        padding-bottom: 5px;
        display: flex;
        align-items: center;
        gap: 8px;
        page-break-after: avoid;
      ">
        <span style="color: #00c1c1;">💓</span>
        SIGNOS VITALES
      </h2>
      
      <div style="display: grid; grid-template-columns: repeat(7, 1fr); gap: 1px; border: 1px solid #ddd; page-break-inside: avoid; break-inside: avoid;">
        ${fieldsHTML}
      </div>
    </div>
  `;
};

// Helper function to generate systems review HTML
const generateSystemsReviewHTML = (systemsReview) => {
  if (!systemsReview) return '';
  
  const systemsFields = [
    { key: 'aparatoDigestivo', label: 'Aparato digestivo' },
    { key: 'aparatoRespiratorio', label: 'Aparato respiratorio' },
    { key: 'aparatoCardiovascular', label: 'Aparato cardiovascular' },
    { key: 'aparatoGenitourinario', label: 'Aparato genitourinario' },
    { key: 'sistemaEndocrino', label: 'Sistema endocrino' },
    { key: 'sistemaHematopoyetico', label: 'Sistema hematopoyético' },
    { key: 'sistemaNervioso', label: 'Sistema nervioso' },
    { key: 'sistemaMusculoEsqueletico', label: 'Sistema músculo esquelético' },
    { key: 'aparatoTegumentario', label: 'Aparato tegumentario' }
  ];
  
  // Check if there's any content in systems review
  const hasContent = systemsFields.some(field => 
    systemsReview[field.key] && (
      systemsReview[field.key].answer !== null || 
      (systemsReview[field.key].details && systemsReview[field.key].details.trim())
    )
  );
  if (!hasContent) return '';
  
  const systemsHTML = systemsFields.map(field => {
    const fieldData = systemsReview[field.key] || { answer: null, details: '' };
    return `
      <div style="display: flex; align-items: center; justify-content: space-between; font-size: 10px; margin-bottom: 8px; border-bottom: 1px solid #eee; padding-bottom: 4px;">
        <span style="flex: 1;">${field.label}</span>
        <div style="display: flex; gap: 8px; align-items: center;">
          <span style="background: ${fieldData.answer === true ? '#9b59b6' : '#f0f0f0'}; color: ${fieldData.answer === true ? 'white' : '#666'}; padding: 2px 8px; border-radius: 3px; font-size: 9px;">Sí</span>
          <span style="background: ${fieldData.answer === false ? '#9b59b6' : '#f0f0f0'}; color: ${fieldData.answer === false ? 'white' : '#666'}; padding: 2px 8px; border-radius: 3px; font-size: 9px;">No</span>
          ${fieldData.answer === true && fieldData.details ? `<span style="font-size: 8px; color: #666; max-width: 200px; overflow: hidden; text-overflow: ellipsis;">¿Cuál? ${fieldData.details}</span>` : ''}
        </div>
      </div>
    `;
  }).join('');
  
  return `
    <div style="margin-top: 20px; page-break-inside: avoid; break-inside: avoid;" data-section="systems-review">
      <!-- Blue separator line -->
      <div style="
        height: 3px; 
        background: linear-gradient(90deg, #00c1c1, #0099aa); 
        margin: 15px 0; 
        border-radius: 2px;
        box-shadow: 0 1px 2px rgba(0, 193, 193, 0.3);
      "></div>
      
      <h2 style="
        margin: 0 0 15px 0; 
        font-size: 14px; 
        font-weight: bold; 
        color: #2c3e50; 
        border-bottom: 2px solid #00c1c1; 
        padding-bottom: 5px;
        display: flex;
        align-items: center;
        gap: 8px;
        page-break-after: avoid;
      ">
        <span style="color: #00c1c1;">🏥</span>
        APARATOS Y SISTEMAS
      </h2>
      
      <div style="page-break-inside: avoid; break-inside: avoid;">
        ${systemsHTML}
      </div>
    </div>
  `;
};

// Create HTML template for the medical history PDF
const createMedicalHistoryHTML = (patient, medicalHistory, clinicInfo = null, userInfo = null, questionnaire = null, physicalExam = null, vitalSigns = null, systemsReview = null, logoUrl = null) => {
  const timezone = clinicInfo?.timezone || 'America/Mexico_City';
  const today = new Date().toLocaleDateString('es-ES', { timeZone: timezone });
  const fullName = `${patient.firstName || ''} ${patient.lastName || ''}`.trim();
  const age = calculateAge(patient.dateOfBirth);
  const birthDate = formatDateEs(patient.dateOfBirth);
  const gender = getGenderLabel(patient.gender);
  const cellPhone = patient.cellPhone || patient.phoneNumber || '';
  const doctorTitle = userInfo?.specialization || userInfo?.title || 'Cirujano Dentista';
  
  // Format clinic info for display
  const clinic = clinicInfo ? {
    name: clinicInfo.name || 'CLÍNICA DENTAL',
    phone: clinicInfo.phoneNumber || '',
    email: clinicInfo.email || '',
    address: clinicInfo.address ? 
      `${clinicInfo.address.street || ''}${clinicInfo.address.street && clinicInfo.address.city ? ', ' : ''}${clinicInfo.address.city || ''}${clinicInfo.address.city && clinicInfo.address.state ? ', ' : ''}${clinicInfo.address.state || ''}` 
      : ''
  } : {
    name: 'CLÍNICA DENTAL',
    address: '',
    phone: '',
    email: ''
  };
  
  return `
    <div id="medical-history-pdf" style="
      width: 210mm;
      min-height: auto;
      margin: 0;
      padding: 15mm;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      font-size: 12px;
      line-height: 1.5;
      color: #333;
      background: white;
      box-sizing: border-box;
      position: relative;
    ">
      <!-- Header Section -->
      <div data-section="header">
        <!-- Clinic Header at the top, centered -->
        <div style="text-align: center; margin-bottom: 15px; border-bottom: 1px solid #00c1c1; padding-bottom: 10px; position: relative; min-height: 80px;">
          ${logoUrl ? `
          <!-- Logo -->
          <div style="position: absolute; top: 0; left: 0; width: 80px; height: 80px; display: flex; align-items: center; justify-content: center; opacity: 0.9;">
            <img src="${logoUrl}" alt="Logo" style="max-width: 100%; max-height: 100%; object-fit: contain;" />
          </div>
          ` : ''}
          <div style="font-weight: bold; font-size: 16px; color: #333; margin-bottom: 2px; letter-spacing: 0.5px;">
            ${clinic.name}
          </div>
          ${clinic.address ? `
            <div style="font-size: 9px; color: #777; margin-bottom: 2px; line-height: 1.2;">
              ${clinic.address}
            </div>
          ` : ''}
          <div style="font-size: 9px; color: #777; line-height: 1.2;">
            ${clinic.phone ? `Tel: ${clinic.phone}` : ''}
            ${clinic.phone && clinic.email ? ' • ' : ''}
            ${clinic.email ? `Email: ${clinic.email}` : ''}
          </div>
        </div>
        
        <!-- Document title and date -->
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 15px;">
          <!-- Left: Document title -->
          <div style="flex: 1;">
            <h1 style="
              margin: 0; 
              font-size: 20px; 
              font-weight: bold; 
              color: #333;
              letter-spacing: 0.5px;
            ">HISTORIA CLÍNICA</h1>
          </div>
          
          <!-- Right: Date only -->
          <div style="font-size: 10px; background-color: #f8f9fa; padding: 8px 15px; border-radius: 6px; border: 1px solid #e9ecef;">
            <div style="text-align: center;">
              <span style="font-weight: 500; color: #666; display: block; margin-bottom: 2px;">Fecha</span>
              <span style="font-weight: bold; color: #333; font-size: 11px;">${today}</span>
            </div>
          </div>
        </div>
        
        <!-- Blue separator line -->
        <div style="
          height: 4px; 
          background: linear-gradient(90deg, #00c1c1, #0099aa); 
          margin: 20px 0; 
          border-radius: 2px;
          box-shadow: 0 2px 4px rgba(0, 193, 193, 0.3);
        "></div>
      </div>
      
      <!-- FICHA DE IDENTIFICACIÓN -->
      <div data-section="identification" style="margin-bottom: 15px; background-color: #fafafa; padding: 12px; border-radius: 8px; border: 1px solid #e9ecef;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
          <h2 style="
            margin: 0; 
            font-size: 12px; 
            font-weight: bold; 
            color: #333;
            border-left: 3px solid #00c1c1;
            padding-left: 8px;
          ">FICHA DE IDENTIFICACIÓN</h2>
        </div>
        
        <!-- Patient information grid -->
        <div style="line-height: 1.8; font-size: 10px;">
          <!-- Row 1 -->
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; border-bottom: 1px solid #eee; padding-bottom: 4px;">
            <div style="display: flex; align-items: center;">
              <span style="font-weight: 500; margin-right: 10px; min-width: 50px;">Nombre:</span>
              <span style="font-weight: bold; color: #333;">${fullName}</span>
            </div>
            <div style="display: flex; align-items: center; margin: 0 20px;">
              <span style="font-weight: 500; margin-right: 10px;">Edad:</span>
              <span style="font-weight: bold; color: #333;">${age}</span>
            </div>
            <div style="display: flex; align-items: center;">
              <span style="font-weight: 500; margin-right: 10px;">Género:</span>
              <span style="font-weight: bold; color: #333;">${gender}</span>
            </div>
          </div>
          
          <!-- Row 2 -->
          <div style="display: flex; justify-content: space-between; margin-bottom: 6px; border-bottom: 1px solid #eee; padding-bottom: 4px;">
            <div style="display: flex; align-items: center; flex: 1;">
              <span style="font-weight: 500; margin-right: 10px; min-width: 100px;">Fecha de nacimiento:</span>
              <span style="color: #333;">${birthDate}</span>
            </div>
            <div style="display: flex; align-items: center; margin-left: 30px;">
              <span style="font-weight: 500; margin-right: 10px;">Ocupación:</span>
              <span style="color: #333;">${patient.occupation || 'No especificado'}</span>
            </div>
          </div>
          
          <!-- Row 3 -->
          <div style="display: flex; flex-wrap: wrap; margin-bottom: 6px; border-bottom: 1px solid #eee; padding-bottom: 4px;">
            <div style="display: flex; align-items: center; flex: 2; min-width: 200px; margin-bottom: 3px;">
              <span style="font-weight: 500; margin-right: 10px;">Dirección:</span>
              <span style="color: #333;">${patient.address?.street || 'No especificado'}</span>
            </div>
            <div style="display: flex; align-items: center; margin: 0 15px 3px 15px;">
              <span style="font-weight: 500; margin-right: 10px;">Ciudad:</span>
              <span style="color: #333;">${patient.address?.city || 'No especificado'}</span>
            </div>
            <div style="display: flex; align-items: center; margin-bottom: 3px;">
              <span style="font-weight: 500; margin-right: 10px;">Estado:</span>
              <span style="color: #333;">${patient.address?.state || 'No especificado'}</span>
            </div>
          </div>
          
          <!-- Additional patient info rows -->
          <div style="display: flex; justify-content: space-between; margin-bottom: 6px; border-bottom: 1px solid #eee; padding-bottom: 4px;">
            <div style="display: flex; align-items: center; flex: 1;">
              <span style="font-weight: 500; margin-right: 10px;">Correo:</span>
              <span style="color: #333;">${patient.email || 'No especificado'}</span>
            </div>
            <div style="display: flex; align-items: center; margin-left: 30px;">
              <span style="font-weight: 500; margin-right: 10px;">Celular:</span>
              <span style="color: #333; font-weight: 600;">${cellPhone || 'No especificado'}</span>
            </div>
          </div>
          
          ${patient.estadoCivil ? `
          <div style="display: flex; align-items: center; margin-bottom: 6px; border-bottom: 1px solid #eee; padding-bottom: 4px;">
            <span style="font-weight: 500; margin-right: 10px;">Estado civil:</span>
            <span style="font-weight: bold; color: #333;">${patient.estadoCivil}</span>
          </div>
          ` : ''}
          
          ${patient.lengua ? `
          <div style="display: flex; align-items: center; margin-bottom: 6px; border-bottom: 1px solid #eee; padding-bottom: 4px;">
            <span style="font-weight: 500; margin-right: 10px;">Lengua:</span>
            <span style="color: #333;">${patient.lengua}</span>
          </div>
          ` : ''}
          
          ${patient.tipoSangre ? `
          <div style="display: flex; align-items: center; margin-bottom: 6px; border-bottom: 1px solid #eee; padding-bottom: 4px;">
            <span style="font-weight: 500; margin-right: 10px;">Tipo de sangre:</span>
            <span style="font-weight: bold; color: #333; background-color: #f8f9fa; padding: 2px 8px; border-radius: 3px;">${patient.tipoSangre}</span>
          </div>
          ` : ''}
          
          ${patient.alergias ? `
          <div style="margin-top: 8px;">
            <div style="display: flex; align-items: flex-start; gap: 10px; padding: 3px 0;">
              <span style="font-weight: 500; color: #666; font-size: 10px; min-width: 60px;">Alergias:</span>
              <span style="font-weight: bold; color: #333; font-size: 10px; flex: 1;">${patient.alergias}</span>
            </div>
          </div>
          ` : ''}
        </div>
      </div>
      
      <!-- Medical Questionnaire Section -->
      ${generateQuestionnaireHTML(questionnaire)}
      
      <!-- Physical Examination Section -->
      ${generatePhysicalExamHTML(physicalExam)}
      
      <!-- Vital Signs Section -->
      ${generateVitalSignsHTML(vitalSigns)}
      
      <!-- Systems Review Section -->
      ${generateSystemsReviewHTML(systemsReview)}
      
      <!-- Medical History Section -->
      ${medicalHistory && medicalHistory.trim() ? `
        <div data-section="medical-history" style="margin-top: 20px; page-break-inside: avoid; break-inside: avoid;">
          <!-- Blue separator line -->
          <div style="
            height: 3px; 
            background: linear-gradient(90deg, #00c1c1, #0099aa); 
            margin: 15px 0; 
            border-radius: 2px;
            box-shadow: 0 1px 2px rgba(0, 193, 193, 0.3);
          "></div>
          
          <h2 style="
            margin: 0 0 15px 0; 
            font-size: 14px; 
            font-weight: bold; 
            color: #2c3e50; 
            border-bottom: 2px solid #00c1c1; 
            padding-bottom: 5px;
            display: flex;
            align-items: center;
            gap: 8px;
            page-break-after: avoid;
          ">
            <span style="color: #00c1c1;">📋</span>
            HISTORIA MÉDICA
          </h2>
          <div style="
            border: 1px solid #00c1c1;
            border-radius: 6px;
            padding: 15px;
            min-height: 100px;
            white-space: pre-wrap;
            font-size: 11px;
            line-height: 1.5;
            background-color: #fafafa;
            color: #333;
            font-family: 'Times New Roman', serif;
            page-break-inside: avoid;
            break-inside: avoid;
          ">${medicalHistory}</div>
        </div>
      ` : ``}

      <!-- Patient Authorization Section -->
      <div data-section="authorization" style="margin-top: 20px; page-break-inside: avoid; break-inside: avoid;">
        <!-- Blue separator line -->
        <div style="
          height: 3px; 
          background: linear-gradient(90deg, #00c1c1, #0099aa); 
          margin: 15px 0; 
          border-radius: 2px;
          box-shadow: 0 1px 2px rgba(0, 193, 193, 0.3);
        "></div>
        
        <div style="background-color: #fafafa; padding: 15px; border-radius: 8px; border: 1px solid #e9ecef; page-break-inside: avoid; break-inside: avoid;">
          <h2 style="
            margin: 0 0 12px 0; 
            font-size: 13px; 
            font-weight: bold; 
            color: #333;
            text-align: center;
            letter-spacing: 1px;
            page-break-after: avoid;
          ">AUTORIZACIÓN DEL PACIENTE</h2>
          
          <div style="font-size: 10px; line-height: 1.5; color: #333; text-align: justify; margin-bottom: 20px; page-break-inside: avoid; break-inside: avoid;">
            <p style="margin-bottom: 10px;">
              Autorizo al C. <span style="border-bottom: 1px solid #333; display: inline-block; min-width: 150px; text-align: center; font-weight: bold;">${userInfo?.name || '___________________________'}</span> ${doctorTitle.toLowerCase()} con cédula profesional <span style="border-bottom: 1px solid #333; display: inline-block; min-width: 60px; text-align: center; font-weight: bold;">${userInfo?.licenseId || '                 '}</span>
              para realizar los tratamientos dentales requeridos, aceptando las complicaciones que pudieran presentarse durante el procedimiento quirúrgico y anestésico.
            </p>
            <p style="margin-bottom: 10px;">
              Declaro que he contestado todas las preguntas con honestidad y según mi conocimiento. Asimismo, he sido informado que los datos suministrados quedan reservados en la presente <strong>Historia Clínica</strong> y amparados en secreto profesional.
            </p>
            <p style="margin-bottom: 0;">
              Libero de toda responsabilidad al ${doctorTitle.toLowerCase()}, cuando por incumplimiento de las indicaciones otorgadas durante el tratamiento surgieran complicaciones, afirmando de acuerdo por lo descrito en esta historia clínica y autorización.
            </p>
          </div>
          
          <!-- Signature lines -->
          <div style="display: flex; justify-content: space-between; margin-top: 25px; page-break-inside: avoid; break-inside: avoid;">
            <div style="text-align: center; flex: 1; margin-right: 15px;">
              <div style="border-bottom: 1px solid #333; width: 150px; margin: 0 auto 8px auto; height: 30px;"></div>
              <div style="font-size: 9px; color: #666; font-weight: 500;">
                Nombre y firma del paciente
              </div>
              <div style="font-size: 8px; color: #888; margin-top: 3px;">
                ${patient.firstName} ${patient.lastName}
              </div>
            </div>
            
            <div style="text-align: center; flex: 1; margin-left: 15px;">
              <div style="border-bottom: 1px solid #333; width: 150px; margin: 0 auto 8px auto; height: 30px;"></div>
              <div style="font-size: 9px; color: #666; font-weight: 500;">
                Nombre y firma del ${doctorTitle.toLowerCase()}
              </div>
              <div style="font-size: 8px; color: #888; margin-top: 3px;">
                ${userInfo?.name || userInfo?.fullName || (userInfo?.firstName && userInfo?.lastName ? `${userInfo.firstName} ${userInfo.lastName}` : clinic?.name) || 'Dr./Dra. _______________'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
};

export const generateMedicalHistoryPDF = async (patient, medicalHistory, clinicInfo = null, userInfo = null, questionnaire = null, physicalExam = null, vitalSigns = null, systemsReview = null, logoUrl = null) => {
  let tempDiv = null;
  try {
    console.log('Starting PDF generation...');
    console.log('Logo URL:', logoUrl);
    
    // Convert logo to base64 if provided (html2canvas requires data URLs or same-origin images)
    let logoBase64 = null;
    if (logoUrl) {
      try {
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        logoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        console.log('Logo converted to base64, size:', Math.round(logoBase64.length / 1024), 'KB');
      } catch (logoError) {
        console.warn('Could not load logo:', logoError);
        logoBase64 = null;
      }
    }
    
    // Create a temporary div to hold our HTML
    tempDiv = document.createElement('div');
    tempDiv.innerHTML = createMedicalHistoryHTML(patient, medicalHistory, clinicInfo, userInfo, questionnaire, physicalExam, vitalSigns, systemsReview, logoBase64);
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '-9999px';
    document.body.appendChild(tempDiv);

    const element = tempDiv.querySelector('#medical-history-pdf');
    
    if (!element) {
      throw new Error('No se pudo crear el contenido del PDF');
    }
    
    console.log('Element created, starting canvas rendering...');
    
    // Create PDF with better page handling
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageHeight = 297; // A4 height in mm
    const pageWidth = 210; // A4 width in mm
    const margins = { top: 10, bottom: 10, left: 10, right: 10 };
    const contentHeight = pageHeight - margins.top - margins.bottom;
    
    // Get all major sections that should not be split
    const sections = element.querySelectorAll('[data-section]');
    let currentY = margins.top;
    let pageNumber = 1;
    
    if (sections.length === 0) {
      console.log('Rendering full content...');
      // Fallback: render entire content at once with improved settings
      const canvas = await html2canvas(element, {
        scale: 1.5, // Reduced from 2 to prevent memory issues
        useCORS: true,
        backgroundColor: '#ffffff',
        width: 794, // A4 width in pixels at 96 DPI
        height: element.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        allowTaint: false,
        foreignObjectRendering: false,
        logging: false
      });
      
      console.log('Canvas created, size:', canvas.width, 'x', canvas.height);

      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      console.log('Converting canvas to image...');
      const imgData = canvas.toDataURL('image/jpeg', 0.85); // Use JPEG with 85% quality instead of PNG
      console.log('Image data size:', Math.round(imgData.length / 1024), 'KB');
      
      // Handle multiple pages if content is tall
      if (imgHeight <= contentHeight) {
        // Single page
        console.log('Adding single page to PDF...');
        pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
      } else {
        // Multiple pages
        console.log('Splitting into multiple pages...');
        let remainingHeight = imgHeight;
        let yOffset = 0;
        let pageCount = 0;
        
        while (remainingHeight > 0) {
          if (pageCount > 0) {
            pdf.addPage();
          }
          
          console.log(`Adding page ${pageCount + 1}...`);
          const currentPageHeight = Math.min(remainingHeight, contentHeight);
          
          // Create a new canvas for this page section
          const pageCanvas = document.createElement('canvas');
          const pageCtx = pageCanvas.getContext('2d');
          
          // const scaleX = canvas.width / imgWidth;
          const scaleY = canvas.height / imgHeight;
          
          pageCanvas.width = canvas.width;
          pageCanvas.height = currentPageHeight * scaleY;
          
          pageCtx.drawImage(
            canvas,
            0, yOffset * scaleY,
            canvas.width, currentPageHeight * scaleY,
            0, 0,
            canvas.width, currentPageHeight * scaleY
          );
          
          const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.85); // Use JPEG
          pdf.addImage(pageImgData, 'JPEG', 0, margins.top, imgWidth, currentPageHeight);
          
          yOffset += currentPageHeight;
          remainingHeight -= currentPageHeight;
          pageCount++;
        }
        console.log(`Added ${pageCount} pages total`);
      }
    } else {
      console.log(`Rendering ${sections.length} sections...`);
      // Section-by-section rendering (if sections are marked)
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        console.log(`Rendering section ${i + 1}/${sections.length}...`);
        
        const canvas = await html2canvas(section, {
          scale: 1.5, // Reduced from 2
          useCORS: true,
          backgroundColor: '#ffffff',
          allowTaint: false,
          foreignObjectRendering: false,
          logging: false
        });
        
        console.log(`Section ${i + 1} canvas:`, canvas.width, 'x', canvas.height);
        
        const imgWidth = pageWidth - margins.left - margins.right;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        // Check if section fits on current page
        if (currentY + imgHeight > pageHeight - margins.bottom && pageNumber > 1) {
          console.log(`Section ${i + 1} doesn't fit, adding new page`);
          pdf.addPage();
          currentY = margins.top;
          pageNumber++;
        }
        
        const imgData = canvas.toDataURL('image/jpeg', 0.85); // Use JPEG
        console.log(`Section ${i + 1} image size:`, Math.round(imgData.length / 1024), 'KB');
        pdf.addImage(imgData, 'JPEG', margins.left, currentY, imgWidth, imgHeight);
        
        currentY += imgHeight + 5; // Add small spacing between sections
        
        if (i === 0) pageNumber++; // After first section
      }
      console.log('All sections rendered');
    }

    // Save the PDF using Blob (more reliable than pdf.save())
    const fileName = `historia_clinica_${patient.firstName}_${patient.lastName}_${new Date().toISOString().split('T')[0]}.pdf`;
    console.log('Saving PDF:', fileName);
    
    // Generate PDF as Blob
    const pdfBlob = pdf.output('blob');
    console.log('PDF Blob size:', Math.round(pdfBlob.size / 1024), 'KB');
    
    // Create download link
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      console.log('PDF download initiated and cleaned up');
    }, 100);
    
    console.log('PDF generation complete');
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    console.error('Error stack:', error.stack);
    throw error; // Re-throw to allow proper error handling in the caller
  } finally {
    // Always clean up the temporary element
    if (tempDiv && tempDiv.parentNode) {
      console.log('Cleaning up temporary element');
      document.body.removeChild(tempDiv);
    }
  }
};

// Helper function to denormalize surface names from DB format to UI format
const denormalizeSurface = (s) => {
  const map = {
    'oclusal/incisal': 'top',
    'distal': 'right',
    'lingual/palatino': 'bottom',
    'mesial': 'left',
    'centro': 'center',
  };
  return map[s] || s;
};

// Helper function to create tooth selection map from dental note
const createToothSelectionMapFromNote = (note) => {
  const selectionMap = {};
  
  // Notas multi-diagnóstico: procesar cada sub-nota
  if (note.subNotes && Array.isArray(note.subNotes)) {
    note.subNotes.forEach((subNote) => {
      if (subNote.selections && Array.isArray(subNote.selections)) {
        subNote.selections.forEach((selection) => {
          const toothLabel = selection.tooth;
          const surface = denormalizeSurface(selection.surface);
          
          if (!selectionMap[toothLabel]) {
            selectionMap[toothLabel] = {};
          }
          
          // Usar el color del diagnóstico si está disponible
          const colorId = subNote.diagnosisColor || 'blue';
          selectionMap[toothLabel][surface] = colorId;
        });
      }
    });
  }
  // Formato tradicional: con array de selections
  else if (note.selections && Array.isArray(note.selections)) {
    note.selections.forEach((selection) => {
      const toothLabel = selection.tooth;
      const surface = denormalizeSurface(selection.surface);
      
      if (!selectionMap[toothLabel]) {
        selectionMap[toothLabel] = {};
      }
      
      // Usar el color del diagnóstico si está disponible
      const colorId = note.diagnosisColor || 'blue';
      selectionMap[toothLabel][surface] = colorId;
    });
  }
  // Formato antiguo: propiedades directas
  else if (note.tooth && note.surface) {
    const toothLabel = note.tooth;
    const surface = denormalizeSurface(note.surface);
    
    if (!selectionMap[toothLabel]) {
      selectionMap[toothLabel] = {};
    }
    
    const colorId = note.diagnosisColor || 'blue';
    selectionMap[toothLabel][surface] = colorId;
  }
  
  return selectionMap;
};

// Helper function to create legend for odontogram
const createOdontogramLegend = (dentalNote) => {
  if (!dentalNote) return '';
  
  const diagnosesUsed = new Map();
  
  // Collect unique diagnoses from the note
  if (dentalNote.subNotes && Array.isArray(dentalNote.subNotes)) {
    dentalNote.subNotes.forEach(subNote => {
      if (subNote.diagnosisId && subNote.diagnosisName) {
        const key = subNote.diagnosisId;
        if (!diagnosesUsed.has(key)) {
          diagnosesUsed.set(key, {
            name: subNote.diagnosisName,
            color: subNote.diagnosisColor || '#gray',
            isCompleteTooth: subNote.isCompleteTooth || false
          });
        }
      }
    });
  } else if (dentalNote.diagnosisId && dentalNote.diagnosisName) {
    diagnosesUsed.set(dentalNote.diagnosisId, {
      name: dentalNote.diagnosisName,
      color: dentalNote.diagnosisColor || '#gray',
      isCompleteTooth: dentalNote.isCompleteTooth || false
    });
  }
  
  if (diagnosesUsed.size === 0) return '';
  
  let legendHTML = `
    <div style="margin-top: 20px; padding: 12px; background-color: #f8f9fa; border-radius: 6px; border: 1px solid #e9ecef;">
      <h3 style="margin: 0 0 12px 0; font-size: 11px; font-weight: bold; color: #2c3e50; border-bottom: 1px solid #00c1c1; padding-bottom: 4px;">
        <span style="color: #00c1c1;">📋</span>
        REFERENCIAS DEL ODONTOGRAMA
      </h3>
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 8px;">
  `;
  
  // DENTAL_COLORS mapping for color conversion
  const DENTAL_COLORS_MAP = {
    'red': { color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.6)' },
    'blue': { color: '#38b6ff', bgColor: 'rgba(56, 182, 255, 0.6)' },
    'green': { color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.6)' },
    'orange': { color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.6)' },
    'purple': { color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.6)' },
    'pink': { color: '#ec4899', bgColor: 'rgba(236, 72, 153, 0.6)' },
    'gray': { color: '#6b7280', bgColor: 'rgba(107, 114, 128, 0.6)' },
    'yellow': { color: '#eab308', bgColor: 'rgba(234, 179, 8, 0.6)' }
  };
  
  const getColorForDisplay = (colorId) => {
    if (!colorId) return '#38b6ff';
    if (colorId.startsWith('#')) return colorId;
    const colorInfo = DENTAL_COLORS_MAP[colorId];
    return colorInfo ? colorInfo.color : '#38b6ff';
  };
  
  diagnosesUsed.forEach((diagnosis, id) => {
    const colorDisplay = getColorForDisplay(diagnosis.color);
    
    // For complete tooth, show X representation
    const representation = diagnosis.isCompleteTooth 
      ? '• Diente completo (representado con X)' 
      : '• Por superficie (coloreado)';
    
    legendHTML += `
      <div style="display: flex; align-items: center; gap: 8px; padding: 6px; background: white; border-radius: 4px; border: 1px solid #e0e0e0;">
        ${diagnosis.isCompleteTooth ? `
          <div style="width: 20px; height: 20px; border-radius: 4px; background-color: #fff; border: 2px solid ${colorDisplay}; flex-shrink: 0; box-shadow: 0 1px 2px rgba(0,0,0,0.1); position: relative; display: flex; align-items: center; justify-content: center;">
            <span style="color: ${colorDisplay}; font-weight: bold; font-size: 12px; line-height: 1;">✕</span>
          </div>
        ` : `
          <div style="width: 20px; height: 20px; border-radius: 4px; background-color: ${colorDisplay}; border: 1px solid #ccc; flex-shrink: 0; box-shadow: 0 1px 2px rgba(0,0,0,0.1);"></div>
        `}
        <div style="flex: 1;">
          <div style="font-size: 9px; font-weight: 600; color: #333; margin-bottom: 2px;">${diagnosis.name}</div>
          <div style="font-size: 8px; color: #666;">
            ${representation}
          </div>
        </div>
      </div>
    `;
  });
  
  legendHTML += `
      </div>
    </div>
  `;
  
  return legendHTML;
};

// Helper function to create HTML for odontogram
const createOdontogramHTML = (toothSelections, mode = 'adult', dentalNote = null) => {
  const adultTeeth = [
    ['1.8','1.7','1.6','1.5','1.4','1.3','1.2','1.1','2.1','2.2','2.3','2.4','2.5','2.6','2.7','2.8'],
    ['4.8','4.7','4.6','4.5','4.4','4.3','4.2','4.1','3.1','3.2','3.3','3.4','3.5','3.6','3.7','3.8'],
  ];
  
  const pediatricTeeth = [
    ['5.5','5.4','5.3','5.2','5.1','6.1','6.2','6.3','6.4','6.5'],
    ['8.5','8.4','8.3','8.2','8.1','7.1','7.2','7.3','7.4','7.5'],
  ];
  
  const rows = mode === 'adult' ? adultTeeth : pediatricTeeth;
  const rowLabels = mode === 'adult'
    ? ['Arco superior (18–28)', 'Arco inferior (48–38)']
    : ['Arco superior (55–65)', 'Arco inferior (85–75)'];
  
  // Import DENTAL_COLORS mapping
  const DENTAL_COLORS_MAP = {
    'red': { color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.6)' },
    'blue': { color: '#38b6ff', bgColor: 'rgba(56, 182, 255, 0.6)' },
    'green': { color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.6)' },
    'orange': { color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.6)' },
    'purple': { color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.6)' },
    'pink': { color: '#ec4899', bgColor: 'rgba(236, 72, 153, 0.6)' },
    'gray': { color: '#6b7280', bgColor: 'rgba(107, 114, 128, 0.6)' },
    'yellow': { color: '#eab308', bgColor: 'rgba(234, 179, 8, 0.6)' }
  };
  
  const getColorInfo = (colorId) => {
    if (colorId && colorId.startsWith('#')) {
      return { color: colorId, bgColor: colorId };
    }
    return DENTAL_COLORS_MAP[colorId] || DENTAL_COLORS_MAP['blue'];
  };
  
  const renderTooth = (label, uniqueId) => {
    const displayLabel = String(label).replace('.', '');
    const selections = toothSelections[label] || {};
    // Reduced size to fit all teeth in one row
    const size = 35;
    const pad = 2;
    const center = size / 2;
    const half = 6;
    
    // Check if complete tooth
    const surfaces = ['top', 'right', 'bottom', 'left', 'center'];
    const surfaceColors = surfaces.map(s => selections[s]).filter(Boolean);
    const isCompleteTooth = surfaceColors.length === 5 && surfaceColors.every(c => c === surfaceColors[0]);
    const completeToothColor = isCompleteTooth ? surfaceColors[0] : null;
    
    const tl_poly = { x: pad, y: pad };
    const tr_poly = { x: size - pad, y: pad };
    const br_poly = { x: size - pad, y: size - pad };
    const bl_poly = { x: pad, y: size - pad };
    const ctl = { x: center - half, y: center - half };
    const ctr = { x: center + half, y: center - half };
    const cbr = { x: center + half, y: center + half };
    const cbl = { x: center - half, y: center + half };
    
    const polyPoints = {
      top: `${tl_poly.x},${tl_poly.y} ${tr_poly.x},${tr_poly.y} ${ctr.x},${ctr.y} ${ctl.x},${ctl.y}`,
      right: `${tr_poly.x},${tr_poly.y} ${br_poly.x},${br_poly.y} ${cbr.x},${cbr.y} ${ctr.x},${ctr.y}`,
      bottom: `${br_poly.x},${br_poly.y} ${bl_poly.x},${bl_poly.y} ${cbl.x},${cbl.y} ${cbr.x},${cbr.y}`,
      left: `${bl_poly.x},${bl_poly.y} ${tl_poly.x},${tl_poly.y} ${ctl.x},${ctl.y} ${cbl.x},${cbl.y}`,
    };
    
    // Use unique ID for clipPath
    const clipPathId = `rounded-mask-${uniqueId}`;
    
    let toothSVG = `
      <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" style="display: block;">
        <defs>
          <clipPath id="${clipPathId}">
            <rect x="${pad}" y="${pad}" width="${size - pad * 2}" height="${size - pad * 2}" rx="6" ry="6" />
          </clipPath>
        </defs>
        <rect x="${pad}" y="${pad}" width="${size - pad * 2}" height="${size - pad * 2}" rx="6" ry="6" fill="#fff" stroke="#4b5563" stroke-width="1.5" />
    `;
    
    if (isCompleteTooth) {
      const colorInfo = getColorInfo(completeToothColor);
      toothSVG += `
        <line x1="${pad + 4}" y1="${pad + 4}" x2="${size - pad - 4}" y2="${size - pad - 4}" stroke="${colorInfo.color}" stroke-width="4" stroke-linecap="round" />
        <line x1="${size - pad - 4}" y1="${pad + 4}" x2="${pad + 4}" y2="${size - pad - 4}" stroke="${colorInfo.color}" stroke-width="4" stroke-linecap="round" />
      `;
    } else {
      ['top', 'right', 'bottom', 'left'].forEach(key => {
        const colorValue = selections[key];
        if (colorValue) {
          const colorInfo = getColorInfo(colorValue);
          toothSVG += `<polygon points="${polyPoints[key]}" fill="${colorInfo.bgColor}" clip-path="url(#${clipPathId})" />`;
        }
      });
      
      if (selections['center']) {
        const colorInfo = getColorInfo(selections['center']);
        toothSVG += `<rect x="${center - half}" y="${center - half}" width="${half * 2}" height="${half * 2}" fill="${colorInfo.bgColor}" stroke="#4b5563" stroke-width="1.5" clip-path="url(#${clipPathId})" />`;
      }
      
      // Divider lines
      const borderOffset = 2;
      const tl_line = { x: pad + borderOffset, y: pad + borderOffset };
      const tr_line = { x: size - pad - borderOffset, y: pad + borderOffset };
      const br_line = { x: size - pad - borderOffset, y: size - pad - borderOffset };
      const bl_line = { x: pad + borderOffset, y: size - pad - borderOffset };
      
      toothSVG += `
        <line x1="${tl_line.x}" y1="${tl_line.y}" x2="${ctl.x}" y2="${ctl.y}" stroke="#4b5563" stroke-width="1.5" />
        <line x1="${tr_line.x}" y1="${tr_line.y}" x2="${ctr.x}" y2="${ctr.y}" stroke="#4b5563" stroke-width="1.5" />
        <line x1="${br_line.x}" y1="${br_line.y}" x2="${cbr.x}" y2="${cbr.y}" stroke="#4b5563" stroke-width="1.5" />
        <line x1="${bl_line.x}" y1="${bl_line.y}" x2="${cbl.x}" y2="${cbl.y}" stroke="#4b5563" stroke-width="1.5" />
      `;
    }
    
    toothSVG += `</svg>`;
    return `<div style="display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; flex: 0 0 auto;"><div>${toothSVG}</div><div style="font-size: 7px; margin-top: 1px;">${displayLabel}</div></div>`;
  };
  
  let html = `
    <div style="background: white; padding: 15px; border-radius: 8px; border: 1px solid #e9ecef; width: 100%;">
      <h2 style="margin: 0 0 15px 0; font-size: 14px; font-weight: bold; color: #2c3e50; border-bottom: 2px solid #00c1c1; padding-bottom: 5px; display: flex; align-items: center; gap: 8px;">
        <span style="color: #00c1c1;">🦷</span>
        ODONTOGRAMA INICIAL
      </h2>
  `;
  
  rows.forEach((row, idx) => {
    html += `
      <div style="margin-bottom: 12px; width: 100%;">
        <div style="font-size: 10px; font-weight: 600; color: #666; margin-bottom: 6px; text-align: center;">${rowLabels[idx]}</div>
        <div style="display: flex; justify-content: space-around; align-items: center; width: 100%; flex-wrap: nowrap; gap: 1px;">
          ${row.map((label, toothIdx) => renderTooth(label, `${mode}-${idx}-${toothIdx}-${label}`)).join('')}
        </div>
        ${idx === 0 ? '<div style="border-bottom: 1px solid #ddd; margin: 12px 0;"></div>' : ''}
      </div>
    `;
  });
  
  html += `</div>`;
  
  // Add legend if dental note is provided
  if (dentalNote) {
    html += createOdontogramLegend(dentalNote);
  }
  
  return html;
};

// Function to generate complete patient record PDF (history + initial odontogram)
export const generatePatientRecordPDF = async (patient, medicalHistory, clinicInfo = null, userInfo = null, questionnaire = null, physicalExam = null, vitalSigns = null, systemsReview = null, initialDentalNote = null, logoUrl = null) => {
  try {
    // Convert logo to base64 if provided
    let logoBase64 = null;
    if (logoUrl) {
      try {
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        logoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (logoError) {
        console.warn('Could not load logo:', logoError);
        logoBase64 = null;
      }
    }

    // Create a temporary div to hold our HTML
    const tempDiv = document.createElement('div');

    // Generate medical history HTML
    let medicalHistoryHTML = createMedicalHistoryHTML(patient, medicalHistory, clinicInfo, userInfo, questionnaire, physicalExam, vitalSigns, systemsReview, logoBase64);
    
    // Change title from "HISTORIA CLÍNICA" to "EXPEDIENTE"
    medicalHistoryHTML = medicalHistoryHTML.replace('HISTORIA CLÍNICA', 'EXPEDIENTE');
    
    // Generate odontogram HTML if initial dental note exists
    let odontogramHTML = '';
    if (initialDentalNote) {
      const toothSelections = createToothSelectionMapFromNote(initialDentalNote);
      const mode = initialDentalNote.mode || 'adult';
      odontogramHTML = createOdontogramHTML(toothSelections, mode, initialDentalNote);
    }
    
    // Insert odontogram before authorization section
    if (odontogramHTML) {
      const odontogramSection = `
        <div data-section="odontogram" style="margin-top: 30px; page-break-inside: avoid; break-inside: avoid;">
          ${odontogramHTML}
        </div>
      `;
      // Find the authorization section and insert odontogram before it
      const authMarker = '<!-- Patient Authorization Section -->';
      const authIndex = medicalHistoryHTML.indexOf(authMarker);
      if (authIndex !== -1) {
        // Insert odontogram right before the authorization section comment
        medicalHistoryHTML = medicalHistoryHTML.substring(0, authIndex) + odontogramSection + medicalHistoryHTML.substring(authIndex);
      } else {
        // Fallback: try to find data-section="authorization"
        const dataSectionIndex = medicalHistoryHTML.indexOf('data-section="authorization"');
        if (dataSectionIndex !== -1) {
          // Find the start of the div (go backwards to find the opening <div)
          let divStart = dataSectionIndex;
          while (divStart > 0 && medicalHistoryHTML.substring(divStart - 4, divStart) !== '<div') {
            divStart--;
          }
          if (divStart > 0) {
            medicalHistoryHTML = medicalHistoryHTML.substring(0, divStart) + odontogramSection + medicalHistoryHTML.substring(divStart);
          } else {
            // If we can't find the start, append before the last closing div
            const lastDivIndex = medicalHistoryHTML.lastIndexOf('</div>');
            if (lastDivIndex !== -1) {
              medicalHistoryHTML = medicalHistoryHTML.substring(0, lastDivIndex) + odontogramSection + medicalHistoryHTML.substring(lastDivIndex);
            }
          }
        } else {
          // If authorization section not found, append at the end before the last closing div
          const lastDivIndex = medicalHistoryHTML.lastIndexOf('</div>');
          if (lastDivIndex !== -1) {
            medicalHistoryHTML = medicalHistoryHTML.substring(0, lastDivIndex) + odontogramSection + medicalHistoryHTML.substring(lastDivIndex);
          }
        }
      }
    }
    
    // Combine both sections
    const combinedHTML = `
      <div id="patient-record-pdf" style="
        width: 210mm;
        min-height: auto;
        margin: 0;
        padding: 15mm;
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        font-size: 12px;
        line-height: 1.5;
        color: #333;
        background: white;
        box-sizing: border-box;
        position: relative;
      ">
        ${medicalHistoryHTML.replace('id="medical-history-pdf"', '')}
      </div>
    `;
    
    tempDiv.innerHTML = combinedHTML;
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '-9999px';
    document.body.appendChild(tempDiv);

    const element = tempDiv.querySelector('#patient-record-pdf');
    
    // Create PDF with better page handling
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageHeight = 297; // A4 height in mm
    const pageWidth = 210; // A4 width in mm
    const margins = { top: 10, bottom: 10, left: 10, right: 10 };
    const contentHeight = pageHeight - margins.top - margins.bottom;
    
    // Get all major sections that should not be split
    const sections = element.querySelectorAll('[data-section]');
    let currentY = margins.top;
    let pageNumber = 1;
    
    if (sections.length === 0) {
      // Fallback: render entire content at once
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        width: 794,
        height: element.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        allowTaint: false,
        foreignObjectRendering: false
      });

      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      const imgData = canvas.toDataURL('image/png', 1.0);
      
      // Handle multiple pages if content is tall
      if (imgHeight <= contentHeight) {
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      } else {
        let remainingHeight = imgHeight;
        let yOffset = 0;
        let pageCount = 0;
        
        while (remainingHeight > 0) {
          if (pageCount > 0) {
            pdf.addPage();
          }
          
          const currentPageHeight = Math.min(remainingHeight, contentHeight);
          const pageCanvas = document.createElement('canvas');
          const pageCtx = pageCanvas.getContext('2d');
          const scaleY = canvas.height / imgHeight;
          
          pageCanvas.width = canvas.width;
          pageCanvas.height = currentPageHeight * scaleY;
          
          pageCtx.drawImage(
            canvas,
            0, yOffset * scaleY,
            canvas.width, currentPageHeight * scaleY,
            0, 0,
            canvas.width, currentPageHeight * scaleY
          );
          
          const pageImgData = pageCanvas.toDataURL('image/png', 1.0);
          pdf.addImage(pageImgData, 'PNG', 0, margins.top, imgWidth, currentPageHeight);
          
          yOffset += currentPageHeight;
          remainingHeight -= currentPageHeight;
          pageCount++;
        }
      }
    } else {
      // Section-by-section rendering
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        
        const canvas = await html2canvas(section, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          allowTaint: false,
          foreignObjectRendering: false
        });
        
        const imgWidth = pageWidth - margins.left - margins.right;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        // Check if section fits on current page
        if (currentY + imgHeight > pageHeight - margins.bottom && pageNumber > 1) {
          pdf.addPage();
          currentY = margins.top;
          pageNumber++;
        }
        
        const imgData = canvas.toDataURL('image/png', 1.0);
        pdf.addImage(imgData, 'PNG', margins.left, currentY, imgWidth, imgHeight);
        
        currentY += imgHeight + 5;
        
        if (i === 0) pageNumber++;
      }
    }

    // Remove the temporary element
    document.body.removeChild(tempDiv);

    // Save the PDF
    const fileName = `expediente_${patient.firstName}_${patient.lastName}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);
    
  } catch (error) {
    console.error('Error generating patient record PDF:', error);
    alert('Error al generar el PDF del expediente. Por favor, inténtalo de nuevo.');
  }
};
